import type { AssistantTranscriptMessageRuntime } from "../types";

/**
 * Transcript identity the backend stamps onto a LIVE stream row.
 *
 * The vendored agent hooks rebuild stream rows client-side and keep only `metadata` from what
 * Convex sent, so everything the transcript needs about a streaming message travels in here
 * (`packages/convex/convex/assistant/transcript/streams.ts`).
 */
export type ClaudiaTranscriptStreamMetadata = {
  threadId?: string;
  sourceOrder?: number;
  promptMessageId?: string | null;
  promptExecutionAttemptId?: string | null;
  promptAttemptNumber?: number | null;
  promptAttemptPhase?: string | null;
  sourceRunId?: string | null;
  messageKind?: "prompt_response";
  transcriptItemKey?: string;
  finality?: "streaming" | "final";
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function readClaudiaTranscriptStreamMetadata(
  message: Pick<AssistantTranscriptMessageRuntime, "metadata">
): ClaudiaTranscriptStreamMetadata | null {
  if (!isRecord(message.metadata)) {
    return null;
  }
  const candidate = isRecord(message.metadata.claudiaTranscript)
    ? message.metadata.claudiaTranscript
    : message.metadata;
  return isRecord(candidate) ? (candidate as ClaudiaTranscriptStreamMetadata) : null;
}

/**
 * The thread a transcript row belongs to, whether the backend projected it or the client
 * assembled it from a live stream.
 *
 * Settled rows carry `threadId` from `toTranscriptMessage`; stream rows carry it in their
 * metadata. A row that resolves to neither is a vendored-cache artifact — the agent hooks keep
 * a per-component stream cache that outlives a thread switch, and rebuild its rows without any
 * thread of their own (LAT-950).
 */
export function resolveClaudiaTranscriptMessageThreadId(
  message: Pick<AssistantTranscriptMessageRuntime, "threadId" | "metadata">
) {
  if (typeof message.threadId === "string") {
    return message.threadId;
  }
  const streamMetadata = readClaudiaTranscriptStreamMetadata(message);
  return typeof streamMetadata?.threadId === "string" ? streamMetadata.threadId : undefined;
}

export function compareClaudiaTranscriptMessages(
  left: Pick<AssistantTranscriptMessageRuntime, "order" | "stepOrder">,
  right: Pick<AssistantTranscriptMessageRuntime, "order" | "stepOrder">
) {
  return left.order === right.order ? left.stepOrder - right.stepOrder : left.order - right.order;
}

export function getClaudiaTranscriptMessageIdentity(
  message: Pick<
    AssistantTranscriptMessageRuntime,
    "role" | "sourceOrder" | "stepOrder" | "promptExecutionAttemptId" | "_id" | "id" | "order"
  >
) {
  if (message._id ?? message.id) {
    return `message:${message._id ?? message.id}`;
  }
  if (message.role === "assistant" && message.promptExecutionAttemptId) {
    return `attempt:${message.promptExecutionAttemptId}:${message.stepOrder}`;
  }
  if (typeof message.sourceOrder === "number") {
    return `transcript:${message.role}:${message.sourceOrder}:${message.stepOrder}`;
  }
  return `order:${message.order}:${message.stepOrder}`;
}

export function buildTranscriptMessageKey(messageId: string) {
  return `msg:${messageId}`;
}
